// The 26 steps, grouped A to G, each against a named person with its dates.
// A step is ticked done only with its evidence and marked not applicable only
// with a reason; Postgres refuses either without it, and refuses anyone but the
// owner or a signatory. Five steps (2, 13, 14, 15, 25) are recorded by the panel
// that does the work, so they carry a pointer here instead of a button.
//
// Every step also has Check (db/migrations/9830): Postgres reads the records the
// step depends on, SimplePay live for 16 and 17, and answers done, missing with
// the reasons, or what only a person can confirm. Checking ticks nothing; when
// the records show the step complete, Tick done with what was found is offered.
import { useState } from 'react'
import { AlertTriangle, CheckCircle2, CircleSlash, RotateCcw, SearchCheck, UserCheck, XCircle } from 'lucide-react'
import { useGetOffboardingStepCheck, useUpdateOffboardingItem } from '../../hooks/backend/offboarding'
import { Button } from '../../lib/shadcn/button'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import { Chip } from '../interviews/Chip'
import { writeErrorMessage } from '../../utils/writeError'
import { formatDay, formatWhen, groupItems, type Item } from '../../utils/offboarding'

const RECORDED_BY_PANEL: Record<number, string> = {
  2: 'Recorded when the letter is sent from the Letter panel below.',
  13: 'Recorded when final pay is prepared in the Final pay panel below.',
  14: 'Recorded with the final pay schedule.',
  15: 'Recorded when final pay is approved in the Final pay panel below.',
  25: 'Recorded with the exit interview below.',
}

export function Checklist({ caseId, items, editable, onChanged }: {
  caseId: string; items: Item[]; editable: boolean; onChanged: () => void
}) {
  return (
    <div className="space-y-4">
      {groupItems(items).map((g) => (
        <section key={g.section} className="rounded-lg border border-border">
          <h3 className="border-b border-border bg-muted/40 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {g.section}. {g.label}
          </h3>
          <ul className="divide-y divide-border">
            {g.items.map((it) => <ItemRow key={it.item_no} caseId={caseId} item={it} editable={editable} onChanged={onChanged} />)}
          </ul>
        </section>
      ))}
    </div>
  )
}

type StepCheck = {
  ok: boolean; item_no: number; step_status: string
  verdict: 'done' | 'missing' | 'needs_person' | 'not_applicable'
  found: string[]; missing: string[]; person: string | null; evidence: string | null; may_tick: boolean; checked_at: string
}

const VERDICT = {
  done: { tone: 'green', label: 'Records show it done', icon: <CheckCircle2 className="h-3 w-3" /> },
  missing: { tone: 'red', label: 'Missing', icon: <XCircle className="h-3 w-3" /> },
  needs_person: { tone: 'amber', label: 'A person has to confirm', icon: <UserCheck className="h-3 w-3" /> },
  not_applicable: { tone: 'neutral', label: 'Does not apply', icon: <CircleSlash className="h-3 w-3" /> },
} as const

function CheckResult({ r, stepStatus }: { r: StepCheck; stepStatus: string }) {
  const v = VERDICT[r.verdict]
  return (
    <div className="space-y-1 rounded-md border border-border bg-muted/30 px-2.5 py-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <Chip tone={v.tone} icon={v.icon}>{v.label}</Chip>
        <span className="text-[11px] text-muted-foreground">checked {formatWhen(r.checked_at)}</span>
      </div>
      {stepStatus === 'done' && r.verdict === 'missing' && (
        <p className="font-medium text-rose-700 dark:text-rose-300">This step is ticked done, but the records disagree:</p>
      )}
      {r.missing.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-4 text-rose-700 dark:text-rose-300">
          {r.missing.map((m) => <li key={m}>{m}</li>)}
        </ul>
      )}
      {r.found.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-4 text-foreground">
          {r.found.map((m) => <li key={m}>{m}</li>)}
        </ul>
      )}
      {r.person && <p className="text-muted-foreground"><span className="font-medium text-foreground">For a person: </span>{r.person}</p>}
    </div>
  )
}

function ItemRow({ caseId, item, editable, onChanged }: { caseId: string; item: Item; editable: boolean; onChanged: () => void }) {
  const save = useUpdateOffboardingItem<{ ok: boolean; case_status: string }>()
  const check = useGetOffboardingStepCheck<StepCheck>()
  const [checked, setChecked] = useState<StepCheck | null>(null)
  const [mode, setMode] = useState<null | 'done' | 'na'>(null)
  const [text, setText] = useState('')
  const canAct = editable && item.can_tick

  async function runCheck() {
    try {
      setChecked(await check.trigger({ case_id: caseId, item_no: item.item_no }, { skipCache: true }))
    } catch (err) {
      toast.error(writeErrorMessage(err))
    }
  }

  async function send(status: 'done' | 'not_applicable' | 'open', evidence = text) {
    try {
      await save.trigger({
        case_id: caseId, item_no: item.item_no, status,
        evidence: status === 'done' ? evidence : null, na_reason: status === 'not_applicable' ? text : null,
      }).result
      toast.success(status === 'done' ? `Step ${item.item_no} done` : status === 'open' ? `Step ${item.item_no} reopened` : `Step ${item.item_no} marked not applicable`)
      setMode(null)
      setText('')
      setChecked(null)
      onChanged()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    }
  }

  return (
    <li className={cn('px-3 py-2.5 text-sm', item.red && 'bg-rose-50/70 dark:bg-rose-950/30')}>
      <div className="flex flex-wrap items-start gap-2">
        <span className="mt-0.5 w-6 shrink-0 text-xs font-semibold tabular-nums text-muted-foreground">{item.item_no}</span>
        <div className="min-w-0 flex-1">
          <p className={cn('font-medium text-foreground', item.status === 'not_applicable' && 'text-muted-foreground line-through decoration-muted-foreground/40')}>
            {item.title}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {item.owner ?? 'No owner'} · {formatDay(item.start_date)} to {formatDay(item.target_date)}
            {item.task_ref ? ` · board task #${item.task_ref}` : ''}
          </p>
          {item.owner_basis && item.status === 'open' && (
            <p className="text-[11px] text-muted-foreground/80">{item.owner_basis}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {item.status === 'done' && <Chip tone="green" icon={<CheckCircle2 className="h-3 w-3" />}>Done</Chip>}
          {item.status === 'not_applicable' && <Chip tone="neutral" icon={<CircleSlash className="h-3 w-3" />}>Not applicable</Chip>}
          {item.status === 'open' && item.red && <Chip tone="red" icon={<AlertTriangle className="h-3 w-3" />}>Overdue</Chip>}
          {item.status === 'open' && !item.red && <Chip tone={item.is_mine ? 'brand' : 'amber'}>{item.is_mine ? 'Yours' : 'Open'}</Chip>}
        </div>
      </div>

      <div className="ml-8 mt-1 space-y-1.5">
        {item.status === 'open' && <p className="text-xs text-muted-foreground">{item.guidance}</p>}
        {item.status === 'done' && item.evidence && (
          <p className="text-xs text-foreground"><span className="font-medium">Evidence: </span>{item.evidence}
            <span className="text-muted-foreground"> ({item.done_by ?? 'unknown'}, {formatWhen(item.done_at)})</span></p>
        )}
        {item.status === 'not_applicable' && item.not_applicable_reason && (
          <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Why not: </span>{item.not_applicable_reason}
            {item.na_automatic ? ' (set by the case from the exit type or the records)' : item.done_by ? ` (${item.done_by})` : ''}</p>
        )}

        {item.status === 'open' && RECORDED_BY_PANEL[item.item_no] && (
          <p className="text-xs italic text-muted-foreground">{RECORDED_BY_PANEL[item.item_no]}</p>
        )}

        {checked && <CheckResult r={checked} stepStatus={item.status} />}

        {mode === null && (
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="outline" disabled={check.loading} onClick={() => void runCheck()}>
              {check.loading ? <Spinner className="mr-1 h-3 w-3" /> : <SearchCheck className="mr-1 h-3 w-3" />}
              {checked ? 'Check again' : 'Check'}
            </Button>
            {canAct && item.status === 'open' && checked?.may_tick && checked.evidence && (
              <Button size="sm" variant="brand" disabled={save.loading} onClick={() => void send('done', checked.evidence!)}>
                {save.loading && <Spinner className="mr-1 h-3 w-3" />} Tick done with what was found
              </Button>
            )}
            {canAct && item.status === 'open' && (
              <>
                <Button size="sm" variant={checked?.may_tick ? 'outline' : 'brand'}
                  onClick={() => { setMode('done'); setText(checked?.evidence ?? '') }}>Tick done</Button>
                <Button size="sm" variant="outline" onClick={() => { setMode('na'); setText('') }}>Not applicable</Button>
              </>
            )}
          </div>
        )}
        {editable && !canAct && item.status === 'open' && !RECORDED_BY_PANEL[item.item_no] && (
          <p className="text-[11px] text-muted-foreground">Only {item.owner ?? 'its owner'} or an offboarding signatory can tick this.</p>
        )}
        {canAct && item.status === 'open' && mode === 'na' && (
          <p className="text-xs text-muted-foreground">Mark it not applicable with the reason.</p>
        )}
        {canAct && mode !== null && (
          <div className="space-y-2 pt-1">
            <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)}
              placeholder={mode === 'done' ? item.evidence_hint : 'Why this step does not apply to this exit'} />
            <div className="flex gap-2">
              <Button size="sm" variant="brand" disabled={!text.trim() || save.loading}
                onClick={() => void send(mode === 'done' ? 'done' : 'not_applicable')}>
                {save.loading && <Spinner className="mr-1 h-3 w-3" />} {mode === 'done' ? 'Save as done' : 'Save as not applicable'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setMode(null)}>Cancel</Button>
            </div>
          </div>
        )}
        {editable && item.can_tick && item.status !== 'open' && (
          <button type="button" className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
            onClick={() => void send('open')} disabled={save.loading}>
            <RotateCcw className="h-3 w-3" /> Reopen
          </button>
        )}
      </div>
    </li>
  )
}
